import { ChevronsRight, ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react";
import { Dispatch, FC, SetStateAction, useMemo, useState } from "react";

interface PaginationProps {
  className?: string;
  total: number;
  page: number;
  itemsPerPage?: number;
  limit: number;
  setPage?: (page: number) => void;
  setItemsPerPage?: (limit: number) => void;
  numberOfButtons?: number;
  showButtons?: boolean;
  showSelect?: boolean;
  selectOptions?: number[];
  /** When true, `total` is a lower bound (more pages may exist). */
  hasMore?: boolean;
  /**
   * Dense footer for large catalogs: short range summary, prev/next around
   * "Page X of Y", and a compact page-size select — no long page-number strip.
   */
  compact?: boolean;
}

/**
 * Pagination component for displaying paginated navigation controls.
 *
 * @param {object} props - Component props.
 * @param {string} [props.className] - Optional additional CSS classes to apply to the root pagination container.
 * @param {number} props.total - Total number of records/items to paginate over.
 * @param {number} props.page - Current active page number (1-based index).
 * @param {number} [props.itemsPerPage] - Number of items per page (controlled externally).
 * @param {number} props.limit - Number of items per page (used internally if `itemsPerPage` is not provided).
 * @param {(page: number) => void} [props.setPage] - Function to update the current page. If not provided, pagination controls that change page are disabled.
 * @param {(itemsPerPage: number) => void} [props.setItemsPerPage] - Function to update the number of items shown per page. Used when `showSelect` prop is true.
 * @param {number} [props.numberOfButtons=7] - Maximum number of page buttons to show in the pagination control.
 * @param {boolean} [props.showButtons=true] - Controls whether pagination buttons (page numbers and next/prev) are shown.
 * @param {boolean} [props.showSelect=false] - Whether to show a dropdown select to change the page size.
 * @param {number[]} [props.selectOptions=[9, 18, 36, 72, 144]] - Array of options for the items-per-page select dropdown.
 * @author Joseph Nartey
 * @author GitHub: https://github.com/devjoemdia
 *
 * @returns {JSX.Element} Pagination UI element.
 */

const Pagination: FC<PaginationProps> = ({
  className,
  total,
  page,
  limit,
  setPage,
  itemsPerPage,
  setItemsPerPage,
  showButtons = true,
  numberOfButtons = 7,
  showSelect = false,
  selectOptions = [9, 18, 36, 72, 144],
  hasMore = false,
  compact = false,
}) => {
  const [showPageJump, setShowPageJump] = useState(false);
  const [jumpToPage, setJumpToPage] = useState(page.toString());

  const start = total === 0 ? 0 : (page - 1) * limit + 1;
  const end = total === 0 ? 0 : Math.min(limit * page, total);
  // When the server hasn't given a catalog total, keep Next alive if this page was full.
  const max = hasMore ? Math.max(page + 1, Math.ceil(total / limit) || 1) : Math.ceil(total / limit);

  const fmt = (n: number) => n.toLocaleString();

  // Calculate start/end page numbers for buttons based on current page and numberOfButtons
  const [startPage, endPage] = useMemo(() => {
    let end = page;
    let start = end - numberOfButtons + 2;

    if (start < 1) {
      start = 1;
      end = Math.min(numberOfButtons, max);
    }

    if (end > max) {
      end = max;
      start = Math.max(1, end - numberOfButtons + 2);
    }

    return [start, end];
  }, [page, max, numberOfButtons]);

  const pages: number[] = [];
  for (let i = startPage; i <= endPage; i++) {
    if (max > 5) {
      if (i !== 1 && i !== max) {
        pages.push(i);
      }
    } else {
      pages.push(i);
    }
  }

  // Always show next page when current page is last in pages array and not max
  if (pages.length && page === pages[pages.length - 1] && page < max) {
    pages.push(page + 1);
  }

  const handlePageClick = (pageNum: number) => {
    if (pageNum > 0 && pageNum <= max && setPage) {
      setPage(pageNum);
    }
  };

  const handlePageSizeChange = (itemsPerPage: number) => {
    if (setItemsPerPage) {
      setItemsPerPage(itemsPerPage);
    }
  };

  const handleJumpToPage = () => {
    const targetPage = parseInt(jumpToPage);
    if (targetPage >= 1 && targetPage <= max) {
      handlePageClick(targetPage);
      setShowPageJump(false);
    }
  };

  const getMobilePages = () => {
    const mobileNumberOfButtons = 3; // Show fewer buttons on mobile
    if (max <= mobileNumberOfButtons) {
      return Array.from({ length: max }, (_, i) => i + 1);
    }

    const mobilePages = [];

    // Always show current page
    mobilePages.push(page);

    // Add previous page if it exists and is not 1
    if (page > 1 && page > 2) {
      mobilePages.unshift(page - 1);
    }

    // Add next page if it exists and is not max
    if (page < max && page < max - 1) {
      mobilePages.push(page + 1);
    }

    // Make sure we have at least first and last
    if (!mobilePages.includes(1)) mobilePages.unshift(1);
    if (!mobilePages.includes(max) && max > 1) mobilePages.push(max);

    return mobilePages.sort((a, b) => a - b).slice(0, mobileNumberOfButtons);
  };

  if (compact) {
    return (
      <nav
        className={`flex flex-wrap items-center justify-between gap-3 ${className ?? ""}`}
        aria-label="Pagination"
      >
        <p className="text-sm text-gray-600">
          {total === 0 ? (
            "No records"
          ) : hasMore ? (
            <>
              <span className="font-medium text-gray-900">
                {fmt(start)}–{fmt(end)}
              </span>
              <span className="text-gray-400"> of </span>
              <span className="font-medium text-gray-900">{fmt(end)}+</span>
            </>
          ) : (
            <>
              <span className="font-medium text-gray-900">
                {fmt(start)}–{fmt(end)}
              </span>
              <span className="text-gray-400"> of </span>
              <span className="font-medium text-gray-900">{fmt(total)}</span>
            </>
          )}
        </p>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center rounded-md border border-gray-200 bg-white">
            <button
              type="button"
              onClick={() => handlePageClick(page - 1)}
              disabled={page <= 1}
              className="inline-flex h-8 w-8 items-center justify-center rounded-l-md text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => {
                setJumpToPage(String(page));
                setShowPageJump((open) => !open);
              }}
              className="h-8 min-w-[7.5rem] border-x border-gray-200 px-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
              title="Jump to page"
            >
              {hasMore ? (
                <>Page {fmt(page)}</>
              ) : (
                <>
                  Page {fmt(page)}
                  <span className="font-normal text-gray-400"> / {fmt(max || 1)}</span>
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => handlePageClick(page + 1)}
              disabled={page >= max || max === 0}
              className="inline-flex h-8 w-8 items-center justify-center rounded-r-md text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {showPageJump && (
            <div className="flex items-center gap-1.5">
              <input
                type="number"
                value={jumpToPage}
                onChange={(e) => setJumpToPage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleJumpToPage();
                  if (e.key === "Escape") setShowPageJump(false);
                }}
                min={1}
                max={hasMore ? undefined : max}
                className="h-8 w-16 rounded-md border border-gray-200 px-2 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                aria-label="Page number"
              />
              <button
                type="button"
                onClick={handleJumpToPage}
                className="h-8 rounded-md bg-blue-700 px-2.5 text-xs font-medium text-white hover:bg-blue-800"
              >
                Go
              </button>
            </div>
          )}

          {showSelect && (
            <select
              onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
              className="h-8 rounded-md border border-gray-200 bg-white px-2 text-xs text-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
              value={limit || itemsPerPage}
              aria-label="Rows per page"
            >
              {selectOptions.map((option) => (
                <option value={option} key={option}>
                  {option} / page
                </option>
              ))}
            </select>
          )}
        </div>
      </nav>
    );
  }

  const rootClass = `
    bg-white py-2 sm:py-1 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0
    ${className ?? ""}
  `;

  return (
    <nav className={rootClass} aria-label="Pagination">
      {/* Summary */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="hidden sm:block">
          <p className="text-sm text-gray-700">
            {total === 0 ? (
              <>No records</>
            ) : hasMore ? (
              <>
                Showing <span className="font-medium">{start}</span> to{" "}
                <span className="font-medium">{end}</span> of{" "}
                <span className="font-medium">{end}+</span> records
              </>
            ) : (
              <>
                Showing <span className="font-medium">{start}</span> to{" "}
                <span className="font-medium">{end}</span> of{" "}
                <span className="font-medium">{total}</span> records
              </>
            )}
          </p>
        </div>
        <div className="sm:hidden">
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-600">
              {hasMore
                ? `Page ${page} • ${end}+ total`
                : `Page ${page} of ${max || 1} • ${total} total`}
            </p>
            <button
              onClick={() => setShowPageJump(!showPageJump)}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
            >
              Jump to page
            </button>
          </div>

          {/* Mobile Page Jump Input */}
          {showPageJump && (
            <div className="mt-2 flex items-center gap-2">
              <input
                type="number"
                value={jumpToPage}
                onChange={(e) => setJumpToPage(e.target.value)}
                min="1"
                max={max}
                className="w-16 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Page"
              />
              <button
                onClick={handleJumpToPage}
                className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              >
                Go
              </button>
              <button
                onClick={() => setShowPageJump(false)}
                className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
            </div>
          )}
        </div>

        {/* Mobile Items Per Page Select */}
        {showSelect && (
          <div className="sm:hidden">
            <select
              onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
              className="w-full bg-white border border-gray-300 text-gray-700 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2"
              value={limit || itemsPerPage}
            >
              {selectOptions.map((option, index) => (
                <option value={option} key={index}>
                  Show {option} per page
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {showButtons && (
        <>
          {/* Desktop Pagination */}
          <div className="hidden sm:flex rounded-md bg-brand-bg gap-1 sm:gap-3 p-1 w-fit overflow-x-auto">
            {/* Prev Button */}
            <button
              onClick={() => handlePageClick(page - 1)}
              disabled={page === 1}
              className="disabled:cursor-not-allowed px-3 py-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:hover:bg-transparent text-sm flex-shrink-0 transition-colors"
              aria-label="Previous page"
            >
              Prev
            </button>

            {/* Always show first page */}
            <button
              onClick={() => handlePageClick(1)}
              className={`px-3 py-2 rounded-lg text-sm font-medium flex-shrink-0 transition-colors ${page === 1 ? "bg-blue-800 text-white" : "hover:bg-gray-100"
                }`}
              aria-current={page === 1 ? "page" : undefined}
              aria-label="Go to first page"
            >
              1
            </button>

            {/* Show leading ellipsis if startPage > 2 */}
            {startPage > 2 && (
              <button
                disabled
                className="px-2 py-2 rounded-lg text-sm font-medium flex-shrink-0 cursor-default select-none"
                aria-hidden="true"
              >
                &hellip;
              </button>
            )}

            {/* Middle page numbers */}
            {pages
              .filter((p) => p !== 1)
              .map((p) => (
                <button
                  key={p}
                  onClick={() => handlePageClick(p)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium min-w-[40px] flex-shrink-0 transition-colors ${page === p ? "bg-blue-800 text-white" : "hover:bg-gray-100"
                    }`}
                  aria-current={page === p ? "page" : undefined}
                  aria-label={`Go to page ${p}`}
                >
                  {p}
                </button>
              ))}

            {/* Show trailing ellipsis if endPage < max - 1 */}
            {endPage < max - 1 && (
              <button
                disabled
                className="px-2 py-2 rounded-lg text-sm font-medium flex-shrink-0 cursor-default select-none"
                aria-hidden="true"
              >
                &hellip;
              </button>
            )}

            {/* Show last page if not already rendered among the middle pages.
                (When max > 5 the middle loop deliberately excludes `max`, so it
                must always be re-added here — including when endPage === max,
                which the old `endPage < max` check wrongly skipped, hiding the
                last page and the current-page highlight on the final page.) */}
            {!hasMore && max !== 1 && !pages.includes(max) && (
              <button
                onClick={() => handlePageClick(max)}
                className={`px-3 py-2 rounded-lg text-sm font-medium flex-shrink-0 transition-colors ${page === max ? "bg-blue-800 text-white" : "hover:bg-gray-100"
                  }`}
                aria-current={page === max ? "page" : undefined}
                aria-label={`Go to last page (${max})`}
              >
                {max}
              </button>
            )}

            {/* Next Button */}
            <button
              onClick={() => handlePageClick(page + 1)}
              disabled={page === max || max === 0}
              className="disabled:cursor-not-allowed px-3 py-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:hover:bg-transparent text-sm flex-shrink-0 transition-colors"
              aria-label="Next page"
            >
              Next
            </button>

            {/* Desktop Select items per page */}
            {showSelect && (
              <select
                onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                className="bg-white border border-gray-300 text-gray-500 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-auto min-w-[100px] p-2.5 flex-shrink-0"
                value={limit || itemsPerPage}
              >
                {selectOptions.map((option, index) => (
                  <option value={option} key={index}>
                    {option} / page
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Mobile Pagination */}
          <div className="sm:hidden">
            <div className="flex items-center justify-between">
              {/* Previous Button */}
              <button
                onClick={() => handlePageClick(page - 1)}
                disabled={page === 1}
                className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors"
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </button>

              {/* Mobile Page Numbers - Compact */}
              <div className="flex items-center gap-1">
                {getMobilePages().map((pageNum, index, array) => (
                  <div key={pageNum} className="flex items-center">
                    <button
                      onClick={() => handlePageClick(pageNum)}
                      className={`min-w-[36px] h-9 px-2 rounded-lg text-sm font-medium transition-colors ${page === pageNum
                        ? "bg-blue-600 text-white"
                        : "text-gray-700 hover:bg-gray-100"
                        }`}
                      aria-current={page === pageNum ? "page" : undefined}
                      aria-label={`Go to page ${pageNum}`}
                    >
                      {pageNum}
                    </button>
                    {/* Show ellipsis between non-consecutive pages */}
                    {index < array.length - 1 && array[index + 1] - pageNum > 1 && (
                      <MoreHorizontal className="h-4 w-4 text-gray-400 mx-1" />
                    )}
                  </div>
                ))}
              </div>

              {/* Next Button */}
              <button
                onClick={() => handlePageClick(page + 1)}
                disabled={page === max || max === 0}
                className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors"
                aria-label="Next page"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            {/* Mobile Quick Navigation */}
            <div className="mt-3 flex items-center justify-center gap-2">
              <button
                onClick={() => handlePageClick(1)}
                disabled={page === 1}
                className="px-2 py-1 text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                First
              </button>
              <span className="text-gray-300">•</span>
              <button
                onClick={() => handlePageClick(Math.max(1, page - 5))}
                disabled={page <= 5}
                className="px-2 py-1 text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                -5
              </button>
              <span className="text-gray-300">•</span>
              <button
                onClick={() => handlePageClick(Math.min(max, page + 5))}
                disabled={page > max - 5}
                className="px-2 py-1 text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                +5
              </button>
              <span className="text-gray-300">•</span>
              <button
                onClick={() => handlePageClick(max)}
                disabled={hasMore || page === max}
                className="px-2 py-1 text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Last
              </button>
            </div>
          </div>
        </>
      )}
    </nav>
  );
};

export default Pagination;